'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@repo/ui'
import { Flame, HelpCircle, MessageCircle, ShieldCheck } from 'lucide-react'

import type {
  AgentOperationalPolicy,
  AgentPolicyMode,
  AgentToolId,
} from '@/types/agents'
import type { Tag } from '@/types/leads'

interface SecurityTabProps {
  mode: AgentPolicyMode
  policy: AgentOperationalPolicy
  tags: Tag[]
  allowedTagIds: string[]
  transferOnRequest: boolean
  transferOnConfusion: boolean
  transferOnHotLead: boolean
  onModeChange: (mode: AgentPolicyMode) => void
  onPolicyChange: (policy: AgentOperationalPolicy) => void
  onAllowedTagsChange: (tagIds: string[]) => void
  onTransferChange: (key: string, value: boolean) => void
}

const TOOLS: Array<{
  id: AgentToolId
  label: string
  description: string
}> = [
  {
    id: 'lead.save_field',
    label: 'Salvar dados do lead',
    description: 'Permite atualizar campos nativos e personalizados.',
  },
  {
    id: 'lead.add_note',
    label: 'Adicionar nota',
    description: 'Permite registrar contexto interno no lead.',
  },
  {
    id: 'lead.add_tag',
    label: 'Adicionar tag',
    description: 'Permite classificar o lead e disparar automações.',
  },
  {
    id: 'schedule.check_availability',
    label: 'Consultar disponibilidade',
    description: 'Permite consultar horários reais das agendas vinculadas.',
  },
  {
    id: 'schedule.book',
    label: 'Criar agendamento',
    description: 'Permite confirmar uma reunião no calendário escolhido.',
  },
  {
    id: 'schedule.reschedule',
    label: 'Reagendar',
    description: 'Permite alterar data ou horário de uma reunião existente.',
  },
  {
    id: 'schedule.cancel',
    label: 'Cancelar agendamento',
    description: 'Permite cancelar uma reunião existente.',
  },
  {
    id: 'cadence.enroll',
    label: 'Inscrever em cadência',
    description: 'Permite iniciar uma cadência ativa para o lead.',
  },
  {
    id: 'attendance.transfer_to_human',
    label: 'Transferir para humano',
    description: 'Permite entregar o atendimento à equipe.',
  },
]

const MODE_HELP: Record<AgentPolicyMode, string> = {
  LEGACY:
    'Mantém o comportamento anterior. As regras abaixo são salvas, mas ainda não controlam as ações.',
  SHADOW:
    'Simula as regras e registra o que seria permitido ou bloqueado, sem impedir ações. Ideal para validar a configuração.',
  ENFORCED:
    'Aplica as regras de verdade. Ações desabilitadas ou sem confirmação exigida serão bloqueadas.',
}

export function SecurityTab({
  mode,
  policy,
  tags,
  allowedTagIds,
  transferOnRequest,
  transferOnConfusion,
  transferOnHotLead,
  onModeChange,
  onPolicyChange,
  onAllowedTagsChange,
  onTransferChange,
}: SecurityTabProps) {
  function updateTool(
    toolId: AgentToolId,
    patch: Partial<AgentOperationalPolicy['tools'][number]>
  ) {
    const existing = policy.tools.find((tool) => tool.toolId === toolId)
    const tools = existing
      ? policy.tools.map((tool) =>
          tool.toolId === toolId ? { ...tool, ...patch } : tool
        )
      : [
          ...policy.tools,
          { toolId, enabled: true, confirmation: 'NONE' as const, ...patch },
        ]
    onPolicyChange({ ...policy, tools })
  }

  function toggleAllowedTag(tagId: string, checked: boolean) {
    const next = checked
      ? [...new Set([...allowedTagIds, tagId])]
      : allowedTagIds.filter((id) => id !== tagId)
    onAllowedTagsChange(next)
  }

  return (
    <div className="flex flex-col gap-8">
      <section>
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-primary mt-0.5 size-5" />
          <div>
            <h3 className="text-sm font-semibold">Aplicação das regras</h3>
            <p className="text-muted-foreground mt-1 text-xs">
              O prompt orienta a conversa. Esta política controla o que o agente
              realmente pode executar no sistema.
            </p>
          </div>
        </div>
        <div className="border-border bg-card mt-4 rounded-lg border p-4">
          <Select value={mode} onValueChange={onModeChange}>
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="LEGACY">Legado</SelectItem>
              <SelectItem value="SHADOW">Sombra</SelectItem>
              <SelectItem value="ENFORCED">Aplicar política</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-muted-foreground mt-3 text-xs leading-relaxed">
            {MODE_HELP[mode]}
          </p>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold">Tags autorizadas para IA</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          A IA só poderá aplicar as tags marcadas aqui. Tags podem disparar
          automações, cadências e conversões.
        </p>
        <div className="border-border bg-card mt-4 overflow-hidden rounded-lg border">
          {tags.length === 0 ? (
            <div className="text-muted-foreground p-4 text-sm">
              Nenhuma tag cadastrada na organização.
            </div>
          ) : (
            tags.map((tag) => {
              const checked = allowedTagIds.includes(tag.id)
              return (
                <div
                  key={tag.id}
                  className="border-border flex items-center gap-4 border-b p-4 last:border-0"
                >
                  <span
                    className="size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{tag.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {checked
                        ? 'Liberada para o agente aplicar automaticamente.'
                        : 'Bloqueada para execução automática pela IA.'}
                    </p>
                  </div>
                  <Switch
                    checked={checked}
                    onCheckedChange={(value) => toggleAllowedTag(tag.id, value)}
                  />
                </div>
              )
            })
          )}
        </div>
        <p className="text-muted-foreground mt-3 text-xs">
          Mesmo com a ação “Adicionar tag” ativa, nenhuma tag será usada se esta
          lista ficar vazia.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-semibold">Ações permitidas</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          Ative o que o agente pode fazer e defina quem precisa confirmar cada
          ação.
        </p>
        <div className="border-border bg-card mt-4 overflow-hidden rounded-lg border">
          {TOOLS.map((tool) => {
            const configured = policy.tools.find(
              (item) => item.toolId === tool.id
            )
            return (
              <div
                key={tool.id}
                className="border-border flex flex-col gap-3 border-b p-4 last:border-0 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{tool.label}</p>
                  <p className="text-muted-foreground text-xs">
                    {tool.description}
                  </p>
                </div>
                <Select
                  value={configured?.confirmation ?? 'NONE'}
                  disabled={!configured?.enabled}
                  onValueChange={(confirmation) =>
                    updateTool(tool.id, {
                      confirmation: confirmation as
                        | 'NONE'
                        | 'CONTACT'
                        | 'HUMAN_APPROVAL',
                    })
                  }
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Sem confirmação</SelectItem>
                    <SelectItem value="CONTACT">
                      Confirmar com o lead
                    </SelectItem>
                    <SelectItem value="HUMAN_APPROVAL">
                      Aprovação da equipe
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Switch
                  checked={configured?.enabled ?? false}
                  onCheckedChange={(enabled) =>
                    updateTool(tool.id, { enabled })
                  }
                />
              </div>
            )
          })}
        </div>
        <p className="text-muted-foreground mt-3 text-xs">
          “Confirmar com o lead” pede uma confirmação explícita na conversa.
          “Aprovação da equipe” impede a execução automática até um humano
          autorizar.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-semibold">Transferência para humano</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          Define situações em que a IA deve parar e entregar a conversa à
          equipe.
        </p>
        <div className="border-border bg-card mt-4 overflow-hidden rounded-lg border px-4">
          {[
            {
              key: 'transferOnRequest',
              label: 'Quando o contato pedir para falar com uma pessoa',
              description: 'Detecta pedidos explícitos de atendimento humano.',
              checked: transferOnRequest,
              icon: MessageCircle,
            },
            {
              key: 'transferOnConfusion',
              label: 'Quando o agente não souber responder',
              description:
                'Transfere quando não há confiança para responder adequadamente.',
              checked: transferOnConfusion,
              icon: HelpCircle,
            },
            {
              key: 'transferOnHotLead',
              label: 'Quando o lead for considerado quente',
              description:
                'Entrega rapidamente oportunidades de alta intenção à equipe.',
              checked: transferOnHotLead,
              icon: Flame,
            },
          ].map((item) => {
            const Icon = item.icon
            return (
              <div
                key={item.key}
                className="border-border flex items-center gap-4 border-b py-4 last:border-0"
              >
                <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-md">
                  <Icon className="text-muted-foreground size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-muted-foreground text-xs">
                    {item.description}
                  </p>
                </div>
                <Switch
                  checked={item.checked}
                  onCheckedChange={(checked) =>
                    onTransferChange(item.key, checked)
                  }
                />
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
